import React, { useEffect, useRef, useState } from 'react'
import { cloneDeep, isUndefined } from 'lodash'
import classNames from 'classnames'

import ScrollableBlock from 'components/ScrollableBlock/ScrollableBlock'

import { CONTROL_KEYS, CONFIRMATION_DIALOG_TYPES, NAVIGATION_TYPES } from '../../constants'

import { SCHEMA_KEY } from '../../schema'

import PanelHeader from '../components/PanelHeader/PanelHeader'
import MobileTongue from '../components/MobileTongue/MobileTongue'

import Group from './components/Group/Group'

import useControls from './hooks/useControls'

import { CONTROLS_COMPONENTS } from './constants'

import './components/Controls/controls.scss'

import styles from './index.module.scss'

const ControlPanel = ({
    schema,
    blockName,
    totalPagesCount,
    methods: {
        openModalByName,
        showConfirmationDialog,
        onSelectSpecialPage,
        onAddStartCountdown,
        onRemoveStartCountdown,
    },
    isMultiplayerGame,
    onUpdate,
    blockData,
    appData,
    controlKey,
    selectedBlockArrayProperty,
    integrations,
    isSecondary,
    onClose,
    isOpacity,
}) => {
    const panelScrollRef = useRef(null)
    const [isMobileOpened, setIsMobileOpened] = useState(false)

    const [data, setData] = useState({
        blockData: {},
        payload: {},
        schemaItemsWithControl: [],
        colorTheme: null,
    })

    const { controls } = useControls({
        isMultiplayerGame,
        controlKey,
        typeIdentifier: !!selectedBlockArrayProperty
            ? `${blockData.t}.${selectedBlockArrayProperty.arrName}`
            : blockData.t,
        blockData,
        appData,
        integrations,
        totalPagesCount,
    })

    useEffect(() => {
        let _blockData = cloneDeep(blockData),
            _schema = cloneDeep(schema),
            _payload = {}

        if (selectedBlockArrayProperty) {
            const { arrName, index, schema: arrayPropertySchema } = selectedBlockArrayProperty
            if (arrName && index >= 0 && arrayPropertySchema) {
                // редактирование подсвойства (элемента в массиве) блока, вместо самого блока
                _blockData = blockData[arrName][index]
                _schema = selectedBlockArrayProperty.schema
                _payload['selectedBlockArrayProperty'] = selectedBlockArrayProperty
            }
        }

        setData({
            blockData: _blockData,
            payload: _payload,
            schemaItemsWithControl: getSchemaItemsWithControl(_schema, _blockData),
            colorTheme: blockData?.colorTheme ? blockData.colorTheme : blockData.pcl || null,
        })
    }, [selectedBlockArrayProperty, blockData, schema])

    const getSchemaItemsWithControl = (s, d) => {
        const result = []
        for (const [key, value] of Object.entries(s)) {
            if (value.control && CONTROLS_COMPONENTS[value.control]) {
                result.push({
                    name: key,
                    value: d[key],
                    valueName: d['name'],
                    schema: value,
                })
            }
        }
        return result
    }

    const onControlUpdate = async (id, data, payload) => {
        if (controlKey === CONTROL_KEYS.APP) {
            if (data.hasOwnProperty(SCHEMA_KEY.isUseAuthorization)) {
                if (!data[SCHEMA_KEY.isUseAuthorization]) {
                    showConfirmationDialog(CONFIRMATION_DIALOG_TYPES.AUTH_DELETE)
                    return
                } else {
                    onUpdate(data)
                    onSelectSpecialPage(NAVIGATION_TYPES.AUTH)
                    return
                }
            }
            if (data.hasOwnProperty(SCHEMA_KEY.isLimitActivePeriod)) {
                if (!data[SCHEMA_KEY.isLimitActivePeriod]) {
                    showConfirmationDialog(CONFIRMATION_DIALOG_TYPES.LIMIT_REMOVE)
                    return
                } else {
                    onSelectSpecialPage(NAVIGATION_TYPES.START)
                }
            }
            if (data.hasOwnProperty(SCHEMA_KEY.isAddCountdown)) {
                if (data[SCHEMA_KEY.isAddCountdown]) {
                    await onAddStartCountdown()
                } else {
                    await onRemoveStartCountdown()
                }
            }
            onUpdate(data)
        } else {
            onUpdate({
                [id]: {
                    data,
                    payload,
                },
            })
        }
    }

    const getSchemaItemByName = (s, n) => s.find(item => item.name === n)

    return (
        <div
            className={classNames('editor-panel', 'editor-panel--controls', {
                'editor-panel--controls--opened': isMobileOpened,
                'editor-panel--controls--is-secondary': isSecondary,
                'editor-panel--controls--is-opacity': isOpacity,
            })}
            onClick={evt => {
                evt.preventDefault()
                evt.stopPropagation()
            }}
        >
            <PanelHeader blockName={blockName} scrollRef={panelScrollRef} isSecondary={isSecondary} onClose={onClose} />
            <MobileTongue isOpened={isMobileOpened} onChange={v => setIsMobileOpened(v)} />
            <ScrollableBlock styles={{ height: 'calc(100% - 56px)', width: '100%' }} scrollableNodeRef={panelScrollRef}>
                {controls.length ? (
                    <ul className={styles.list}>
                        {controls.map((group, i) => {
                            let values = []
                            if (group.values) {
                                values = group.values
                                    .map(item => {
                                        const el = getSchemaItemByName(data.schemaItemsWithControl, item.value)
                                        if (!el) return item

                                        if (!isUndefined(item.fieldValue) && item.fieldValue !== el.value) {
                                            onControlUpdate(blockData.id, { [el.name]: item.fieldValue })
                                        }
                                        return item
                                    })
                                    .filter(value => value.isVisible)
                            }

                            if (!group.isVisible || (!values.length && !group.tab)) return null

                            return (
                                <Group
                                    group={group}
                                    key={i}
                                    schema={data.schemaItemsWithControl}
                                    values={values}
                                    blockId={blockData.id}
                                    onUpdate={onControlUpdate}
                                    openModalByName={openModalByName}
                                >
                                    {!!group.label && <p className={styles.groupLabel}>{group.label}</p>}
                                    <ul className={styles.groupList}>
                                        {values.map(item => {
                                            const el = getSchemaItemByName(data.schemaItemsWithControl, item.value)
                                            if (!el) return null

                                            const Component = CONTROLS_COMPONENTS[el.schema.control]
                                            return (
                                                <li className={styles.groupItem} key={item.value}>
                                                    <Component
                                                        id={blockData.id}
                                                        blockType={blockData.t}
                                                        isEnableRating={blockData.isEnableRating}
                                                        name={el.name}
                                                        value={el.value}
                                                        otherValues={data.schemaItemsWithControl}
                                                        valueName={el.valueName}
                                                        tooltipText={item.tooltipText}
                                                        tooltipCallback={item.tooltipCallback}
                                                        descriptionText={item.descriptionText}
                                                        schemaName={item.value}
                                                        schema={el.schema}
                                                        onUpdate={onControlUpdate}
                                                        payload={data.payload}
                                                        openModalByName={openModalByName}
                                                        colorTheme={data.colorTheme}
                                                        fontFamily={blockData.fontFamily}
                                                        leadFormFontFamily={blockData.leadFormFontFamily}
                                                        leadFormBackgroundColor={blockData.leadFormBackgroundColor}
                                                        disabled={group.isDisabled || item.isDisabled}
                                                        isMultiplayerGame={isMultiplayerGame}
                                                    />
                                                    {!!item.note && <div className={styles.note}>{item.note}</div>}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                </Group>
                            )
                        })}
                    </ul>
                ) : (
                    <p className="empty">No additional settings</p>
                )}
            </ScrollableBlock>
        </div>
    )
}

export default ControlPanel
